import { z } from 'zod';

import type { EvalResult } from '../env.ts';
import { type AstInput, nodePos } from '../normalize.ts';
import { opMeta, type SpecialCtx } from '../op-meta.ts';
import { paramPath } from '../path.ts';
import { astArg } from '../schema/ast-codec.ts';
import { expectDataset, expectLazy, isMaterialized } from '../value.ts';

/**
 * `(materialize ds)` — the explicit force.
 *
 * Special op: re-evaluates its argument, then forces materialisation
 * if the result is a `LazyDataset`. Pass-through if the result is
 * already materialised (no-op identity). Errors out via
 * `TypeMismatchError` if the result isn't a `Dataset` at all.
 *
 * Routes through `ctx.materialize` (a `SpecialCtx` callback the
 * evaluator binds to `materialize.ts`'s `materialize()` with the
 * surrounding script's `ops`/`verbs` already curried in). The callback
 * indirection keeps `builtins.ts` decoupled from `materialize.ts` —
 * if we imported `materialize()` directly here, `builtins.ts` →
 * `materialize.ts` → `evaluator.ts` → `builtins.ts` would close a
 * load-order cycle that vitest's ESM loader can't resolve.
 *
 * Implementation lives in `materialize.ts`; this file owns only the
 * op-surface registration so `materializeOpForm` participates in the
 * canonical `builtins` array next to every other top-level op.
 */
export const materializeOpForm = z
  .object({
    name: z.literal('materialize'),
    params: z.tuple([astArg('<dataset-expr>')]),
  })
  .describe('Force materialisation of a (possibly lazy) Dataset. Returns the materialised Dataset.')
  .register(opMeta, {
    docHidden: true,
    evaluation: 'special',
    effect: 'io',
    example: { expr: '(materialize wells)' },
    apply: async (data, ctx: SpecialCtx): Promise<EvalResult> => {
      const dsPath = paramPath(ctx.path, 0);
      const { value } = await ctx.evalNode(data.params[0] as AstInput, ctx.env, dsPath);
      const ds = expectDataset(value, '(materialize <Dataset>)', nodePos(ctx.spans, dsPath));
      // `isMaterialized` narrowing pairs with `Dataset`'s two-variant
      // discrimination: the `else` branch is guaranteed `LazyDataset`
      // by the type system (covered by `value.test.ts`'s discrimination
      // suite). `expectLazy` makes the narrowing explicit to TS and
      // fails loudly if a future third Dataset state appears.
      if (isMaterialized(ds)) {
        return { value: ds, env: ctx.env };
      }
      const lazy = expectLazy(ds, '(materialize <Dataset>)', nodePos(ctx.spans, dsPath));
      const result = await ctx.materialize(lazy);
      return { value: result, env: ctx.env };
    },
  });
